import type { Language } from '@hemmafixa/ui';
import { formatSek } from '@hemmafixa/shared';
import { useMemo, type ReactNode } from 'react';
import { useLanguage } from '@/lib/preferences';
import { I18nContext, type I18n } from './context';
import { en } from './en';
import { sv, type Messages } from './sv';

const messages: Record<Language, Messages> = { sv, en };
const locales = { sv: 'sv-SE', en: 'en-SE' } as const;

/** Holds the interface language for the whole app; switching it re-renders every text. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const { language, setLanguage } = useLanguage();

  const value = useMemo<I18n>(() => {
    const locale = locales[language];
    const dateFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    const dateTimeFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
    const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return {
      language,
      setLanguage,
      t: messages[language],
      money: (ore) => formatSek(ore, locale),
      date: (iso) => dateFormat.format(new Date(iso)),
      dateTime: (iso) => dateTimeFormat.format(new Date(iso)),
      relative: (iso) => {
        const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
        if (Math.abs(minutes) < 60) return relativeFormat.format(minutes, 'minute');
        const hours = Math.round(minutes / 60);
        if (Math.abs(hours) < 24) return relativeFormat.format(hours, 'hour');
        return relativeFormat.format(Math.round(hours / 24), 'day');
      },
    };
  }, [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
